// Font registry - id -> { label, stack, google? }. Web-safe/system fonts have no `google` and
// preview instantly with zero network cost. The others are hosted by Re:Vault itself
// (tools/self-fonts.mjs: Latin and Latin Extended, from /fonts/g/, described by /fonts/css2, which
// takes the same "Family:wght@400;600" specs as the Google Fonts API it replaced, so `google` keeps
// its name). They load lazily: only the selected one in the app (default Verdana loads none), and
// all of them on the settings page for the dropdown previews. No page ever asks a third party.

export const FONTS = {
	verdana: { label: "Verdana", stack: "Verdana,Geneva,sans-serif" },
	system: { label: "System UI", stack: 'system-ui,"Segoe UI",Roboto,sans-serif' },
	arial: { label: "Arial", stack: "Arial,Helvetica,sans-serif" },
	tahoma: { label: "Tahoma", stack: "Tahoma,Geneva,sans-serif" },
	trebuchet: { label: "Trebuchet MS", stack: '"Trebuchet MS",sans-serif' },
	georgia: { label: "Georgia", stack: 'Georgia,"Times New Roman",serif' },
	times: { label: "Times New Roman", stack: '"Times New Roman",Times,serif' },
	garamond: { label: "Garamond", stack: 'Garamond,"Times New Roman",serif' },
	palatino: { label: "Palatino", stack: '"Palatino Linotype","Book Antiqua",Palatino,serif' },
	courier: { label: "Courier New", stack: '"Courier New",Courier,monospace' },
	consolas: { label: "Consolas", stack: 'Consolas,"Courier New",monospace' },
	comic: { label: "Comic Sans", stack: '"Comic Sans MS","Comic Sans",cursive' },
	impact: { label: "Impact", stack: "Impact,Haettenschweiler,sans-serif" },
	mono: { label: "Monospace", stack: 'ui-monospace,"Cascadia Code",Consolas,monospace' },
	// Web fonts, hosted by Re:Vault (lazy-loaded). `rounded` is Figtree (default "Rounded" choice).
	rounded: { label: "Figtree", stack: '"Figtree",system-ui,sans-serif', google: "Figtree:wght@400;600;700" },
	inter: { label: "Inter", stack: '"Inter",system-ui,sans-serif', google: "Inter:wght@400;600" },
	poppins: { label: "Poppins", stack: '"Poppins",system-ui,sans-serif', google: "Poppins:wght@400;600" },
	jakarta: { label: "Plus Jakarta Sans", stack: '"Plus Jakarta Sans",system-ui,sans-serif', google: "Plus+Jakarta+Sans:wght@400;600" },
	outfit: { label: "Outfit", stack: '"Outfit",system-ui,sans-serif', google: "Outfit:wght@400;600" },
	dmsans: { label: "DM Sans", stack: '"DM Sans",system-ui,sans-serif', google: "DM+Sans:wght@400;600" },
	lexend: { label: "Lexend", stack: '"Lexend",system-ui,sans-serif', google: "Lexend:wght@400;600" },
	nunito: { label: "Nunito", stack: '"Nunito",system-ui,sans-serif', google: "Nunito:wght@400;700" },
	quicksand: { label: "Quicksand", stack: '"Quicksand",system-ui,sans-serif', google: "Quicksand:wght@400;600" },
	comfortaa: { label: "Comfortaa", stack: '"Comfortaa",system-ui,cursive', google: "Comfortaa:wght@400;700" },
	fredoka: { label: "Fredoka", stack: '"Fredoka",system-ui,sans-serif', google: "Fredoka:wght@400;600" },
	montserrat: { label: "Montserrat", stack: '"Montserrat",system-ui,sans-serif', google: "Montserrat:wght@400;600" },
	spacegrotesk: { label: "Space Grotesk", stack: '"Space Grotesk",system-ui,sans-serif', google: "Space+Grotesk:wght@400;600" },
	lora: { label: "Lora", stack: '"Lora",Georgia,serif', google: "Lora:wght@400;600" },
	merriweather: { label: "Merriweather", stack: '"Merriweather",Georgia,serif', google: "Merriweather:wght@400;700" },
	playfair: { label: "Playfair Display", stack: '"Playfair Display",Georgia,serif', google: "Playfair+Display:wght@400;600" },
	robotoslab: { label: "Roboto Slab", stack: '"Roboto Slab",Georgia,serif', google: "Roboto+Slab:wght@400;600" },
	sourcecode: { label: "Source Code Pro", stack: '"Source Code Pro",monospace', google: "Source+Code+Pro:wght@400;600" },
	jetbrains: { label: "JetBrains Mono", stack: '"JetBrains Mono",monospace', google: "JetBrains+Mono:wght@400;600" },
	sora: { label: "Sora", stack: '"Sora",system-ui,sans-serif', google: "Sora:wght@400;600" },
	manrope: { label: "Manrope", stack: '"Manrope",system-ui,sans-serif', google: "Manrope:wght@400;600" },
	baloo: { label: "Baloo 2", stack: '"Baloo 2",system-ui,cursive', google: "Baloo+2:wght@400;600" },
	chewy: { label: "Chewy", stack: '"Chewy",system-ui,cursive', google: "Chewy" },
	oswald: { label: "Oswald", stack: '"Oswald",system-ui,sans-serif', google: "Oswald:wght@400;600" },
	rajdhani: { label: "Rajdhani", stack: '"Rajdhani",system-ui,sans-serif', google: "Rajdhani:wght@500;600" },
	orbitron: { label: "Orbitron", stack: '"Orbitron",system-ui,sans-serif', google: "Orbitron:wght@400;700" },
	russo: { label: "Russo One", stack: '"Russo One",system-ui,sans-serif', google: "Russo+One" },
	cormorant: { label: "Cormorant", stack: '"Cormorant",Georgia,serif', google: "Cormorant:wght@500;600" },
	caveat: { label: "Caveat", stack: '"Caveat",cursive', google: "Caveat:wght@500;700" },
	// Designed for low vision (Braille Institute): letters that can't be mistaken for each other.
	atkinson: { label: "Atkinson Hyperlegible", stack: '"Atkinson Hyperlegible",Verdana,sans-serif', google: "Atkinson+Hyperlegible:wght@400;700" },
	// Zen look. Google serves these in unicode-range slices, so a Latin page only pulls the Latin files.
	"zen-gothic": { label: "Zen Kaku Gothic", stack: '"Zen Kaku Gothic New","Segoe UI",system-ui,sans-serif', google: "Zen+Kaku+Gothic+New:wght@400;500;700" },
};

// Heading faces some themes pair with the body font (used through --font-display in theme.css).
export const DISPLAY_FONTS = { zen: "Shippori+Mincho:wght@500;600", "zen-paper": "Shippori+Mincho:wght@500;600" };

export const DEFAULT_FONT = "verdana";

const loaded = new Set();
// The 404 page says data-fonts="none": it can show up inside a site's frame at the site's address,
// so it never loads a web font from there.
const noWebFonts = () => typeof document !== "undefined" && document.documentElement && document.documentElement.dataset.fonts === "none";
export const fontCssUrl = (families) => "/fonts/css2?" + families.map((f) => "family=" + f).join("&") + "&display=swap";
// Study Hall also inlines its look fonts (hall.css) for its first paint; the same files, so a second
// copy of the rules costs nothing.
function inject(families) {
	const key = families.join("|");
	if (loaded.has(key) || noWebFonts()) return;
	loaded.add(key);
	const l = document.createElement("link");
	l.rel = "stylesheet";
	l.href = fontCssUrl(families);
	document.head.appendChild(l);
}

// Load the web font for a single font id (no-op for system fonts).
export function loadFont(id) {
	const f = FONTS[id];
	if (f && f.google) inject([f.google]);
}

// Load the heading face a theme uses, if it has one.
export function loadDisplayFont(theme) {
	const f = DISPLAY_FONTS[theme];
	if (f) inject([f]);
}

// Load every web font once - for the settings-page dropdown previews.
export function loadPreviewFonts() {
	const fams = Object.values(FONTS).filter((f) => f.google).map((f) => f.google);
	if (fams.length) inject(fams);
}
